import { useEffect, useState, type CSSProperties } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, NavLink, Outlet, useMatch, useNavigate } from 'react-router-dom';
import {
  ChevronDown,
  Home,
  LogOut,
  Menu,
  MessageCircle,
  Search,
  Settings,
  X,
} from 'lucide-react';
import { useSession, useLogout } from '@/features/auth/use-session';
import { useChatUnread } from '@/features/chat/api';
import { useAppBadge } from '@/hooks/use-app-badge';
import { usePermission } from '@/hooks/use-permission';
import { Avatar } from '@/components/ui/misc';
import { Button } from '@/components/ui/button';
import { PushPrompt } from '@/components/shared/push-prompt';
import { apiList } from '@/lib/api';
import { onServiceWorkerMessage } from '@/lib/pwa';
import { cn } from '@/lib/utils';
import { CommandPalette } from './command-palette';
import { NotificationBell } from './notification-bell';
import { ImpersonationBanner } from './impersonation-banner';
import { useHomeRoute, useQuickItems, useVisibleSections, type NavItem } from './nav';

export function AppShell() {
  const { data: user } = useSession();
  const logout = useLogout();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const visibleSections = useVisibleSections();
  const home = useHomeRoute();

  // Inside a conversation the composer owns the bottom of the screen, the way
  // it does in any messaging app; the tab bar comes back with the list.
  const inConversation = Boolean(useMatch('/chat/:channelId'));

  // The number on the app icon is the unread in-app notifications — the same
  // count the push messages carry, so the badge never disagrees with itself.
  // Shares the bell's query, so this costs no extra request.
  const { data: notifications } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => apiList<{ unread: number }>('/notifications', { params: { limit: 10 } }),
    refetchInterval: 60_000,
  });
  useAppBadge(notifications?.data.unread);

  // A tap on a notification while the app is open: the worker cannot route,
  // so it hands the link here.
  useEffect(() => onServiceWorkerMessage((message) => navigate(message.link)), [navigate]);

  return (
    <div
      className="flex h-dvh w-full overflow-hidden bg-background"
      style={inConversation ? ({ '--bottom-nav': '0px' } as CSSProperties) : undefined}
    >
      {/* Mobile scrim */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-hidden
        />
      )}

      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex w-64 shrink-0 flex-col bg-sidebar text-sidebar-foreground transition-transform lg:static lg:translate-x-0',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div className="flex h-16 items-center justify-between border-b border-white/10 px-4">
          {/* The mark is the product; the name beside it is the tenant. */}
          <Link to={home} className="flex min-w-0 items-center gap-2.5">
            <span
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary text-[11px] font-black tracking-tight text-primary-foreground"
              aria-hidden
            >
              ORO
            </span>
            <span className="min-w-0 leading-tight">
              <span className="block truncate text-[13px] font-extrabold tracking-tight text-white">
                {user?.organization?.name ?? 'ORO STAR'}
              </span>
              <span className="block text-[10px] font-semibold uppercase tracking-[0.18em] text-primary">
                Portal
              </span>
            </span>
          </Link>
          <button
            className="text-white/70 lg:hidden"
            onClick={() => setSidebarOpen(false)}
            aria-label="Close menu"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="scrollbar-thin flex-1 overflow-y-auto px-2 py-3">
          {visibleSections.map((section) => (
            <div key={section.label} className="mb-4">
              <div className="mb-1 px-2 text-[11px] font-semibold uppercase tracking-wider text-white/40">
                {section.label}
              </div>
              {section.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  onClick={() => setSidebarOpen(false)}
                  className={({ isActive }) =>
                    cn(
                      'mb-0.5 flex items-center gap-2.5 rounded-md px-2 py-2 text-sm transition-colors',
                      isActive
                        ? 'bg-sidebar-accent font-medium text-white'
                        : 'text-sidebar-foreground hover:bg-white/10 hover:text-white',
                    )
                  }
                >
                  <item.icon className="h-4 w-4 shrink-0" />
                  <span className="truncate">{item.label}</span>
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <div className="border-t border-white/10 p-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
          <Link
            to="/account"
            onClick={() => setSidebarOpen(false)}
            className="flex items-center gap-2 rounded-md px-2 py-2 text-sm text-sidebar-foreground transition-colors hover:bg-white/10 hover:text-white"
          >
            <Settings className="h-4 w-4" />
            My account
          </Link>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <ImpersonationBanner />

        <header className="flex h-14 shrink-0 items-center gap-3 border-b bg-background px-4 lg:px-6">
          <button
            className="lg:hidden"
            onClick={() => setSidebarOpen(true)}
            aria-label="Open menu"
          >
            <Menu className="h-5 w-5" />
          </button>

          <button
            onClick={() => setPaletteOpen(true)}
            className="flex h-9 flex-1 max-w-md items-center gap-2 rounded-md border bg-muted/40 px-3 text-sm text-muted-foreground transition-colors hover:bg-muted"
          >
            <Search className="h-4 w-4" />
            <span className="truncate">Search…</span>
            <kbd className="ml-auto hidden rounded border bg-background px-1.5 py-0.5 text-[10px] font-medium sm:inline">
              Ctrl K
            </kbd>
          </button>

          <div className="ml-auto flex items-center gap-1">
            <NotificationBell />

            <div className="relative">
              <button
                onClick={() => setMenuOpen((open) => !open)}
                className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors hover:bg-accent"
              >
                <Avatar name={user?.name ?? '?'} size={26} />
                <span className="hidden max-w-[10rem] truncate sm:inline">{user?.name}</span>
                <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
              </button>

              {menuOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} aria-hidden />
                  <div className="absolute right-0 z-50 mt-1 w-56 rounded-md border bg-popover p-1 shadow-lg">
                    <div className="border-b px-3 py-2">
                      <div className="truncate text-sm font-medium">{user?.name}</div>
                      <div className="truncate text-xs text-muted-foreground">{user?.email}</div>
                      {user?.roles?.length ? (
                        <div className="mt-1 truncate text-xs text-muted-foreground">
                          {user.roles?.map((r) => r.name).join(', ')}
                        </div>
                      ) : null}
                    </div>
                    <button
                      onClick={() => {
                        setMenuOpen(false);
                        navigate('/account');
                      }}
                      className="flex w-full items-center gap-2 rounded-sm px-3 py-2 text-sm hover:bg-accent"
                    >
                      <Settings className="h-4 w-4" />
                      Account settings
                    </button>
                    <button
                      onClick={() => logout.mutate()}
                      className="flex w-full items-center gap-2 rounded-sm px-3 py-2 text-sm text-destructive hover:bg-destructive/10"
                    >
                      <LogOut className="h-4 w-4" />
                      Sign out
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </header>

        {/* The bottom padding leaves room for the phone's tab bar; on a desktop
            the variable is zero and the padding is the ordinary 1.5rem. */}
        <main className="scrollbar-thin flex-1 overflow-y-auto p-4 pb-[calc(1rem+var(--bottom-nav))] lg:p-6 lg:pb-6">
          <div className="mx-auto max-w-6xl">
            {/* Not inside a conversation, where every pixel is the chat's. */}
            {!inConversation && <PushPrompt />}
            <Outlet />
          </div>
        </main>

        {!inConversation && <MobileTabBar home={home} onMenu={() => setSidebarOpen(true)} />}
      </div>

      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
    </div>
  );
}

/**
 * The phone's tab bar: home, the two screens this person opens most, chat
 * with its unread count, and the full menu. Hidden from `md` up, where the
 * sidebar is on screen anyway.
 */
function MobileTabBar({ home, onMenu }: { home: string; onMenu: () => void }) {
  const { can } = usePermission();
  const quick = useQuickItems();
  const hasChat = can('chat.channel.view');
  const { data: unread } = useChatUnread(hasChat);
  const chatUnread = unread?.total ?? 0;

  const tabs: (NavItem & { badge?: number })[] = [
    { to: home, label: 'Home', icon: Home, end: true },
    ...quick.filter((item) => item.to !== home),
    ...(hasChat ? [{ to: '/chat', label: 'Chat', icon: MessageCircle, badge: chatUnread }] : []),
  ];

  return (
    <nav
      className="fixed inset-x-0 bottom-0 z-30 flex h-[var(--bottom-nav)] items-stretch border-t bg-background/95 pb-[env(safe-area-inset-bottom)] backdrop-blur md:hidden"
      aria-label="Primary"
    >
      {tabs.map((tab) => (
        <NavLink
          key={tab.to}
          to={tab.to}
          end={tab.end}
          className={({ isActive }) =>
            cn(
              'relative flex flex-1 flex-col items-center justify-center gap-0.5 text-[10px] font-medium',
              isActive ? 'text-primary' : 'text-muted-foreground',
            )
          }
        >
          <span className="relative">
            <tab.icon className="h-5 w-5" />
            {tab.badge ? (
              <span className="absolute -right-2.5 -top-1.5 min-w-[1.1rem] rounded-full bg-primary px-1 text-center text-[10px] font-semibold leading-4 text-primary-foreground">
                {tab.badge > 99 ? '99+' : tab.badge}
              </span>
            ) : null}
          </span>
          <span className="truncate">{tab.label}</span>
        </NavLink>
      ))}
      <button
        type="button"
        onClick={onMenu}
        className="flex flex-1 flex-col items-center justify-center gap-0.5 text-[10px] font-medium text-muted-foreground"
      >
        <Menu className="h-5 w-5" />
        Menu
      </button>
    </nav>
  );
}

/** Exported for the empty-state buttons that want the same styling. */
export { Button as ShellButton };
